import {noop} from 'lodash';
import React, {FC, useState} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../../../libs/shared/helpers/src/internationalization/react';
import {useToggle} from '../../../../../../libs/shared/helpers/src/react/hooks/hookHelpers';
import {Handler} from '../../../../../../libs/shared/helpers/src/react/reactHelpers';
import {isDefined} from '../../../../../../libs/shared/helpers/src/typescript/typeHelpers';
import {CheckBox} from '../../../../../../libs/shared/ui/src/components/booleanInputs/checkBox';
import {BoxedInput} from '../../../../../../libs/shared/ui/src/components/boxedInputs/boxedInput';
import {BoxedInputElement} from '../../../../../../libs/shared/ui/src/components/boxedInputs/boxedInputElement';
import {ButtonVariantsEnum} from '../../../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {ButtonGroupSpacer} from '../../../../../../libs/shared/ui/src/components/buttons/buttonGroup/buttonGroupSpacer';
import {ChromelessButton} from '../../../../../../libs/shared/ui/src/components/buttons/chromelessButton';
import {SameWidthButton} from '../../../../../../libs/shared/ui/src/components/buttons/sameWidthButton';
import {InputTypesEnum} from '../../../../../../libs/shared/ui/src/components/inputs/input';
import {VisualSizesEnum} from '../../../../../../libs/shared/ui/src/styles/commonStyles';
import {fontSizes, fontWeights} from '../../../../../../libs/shared/ui/src/styles/fontStyles';
import {AlertDialog} from '../../../../../components/common/dialogs/alertDialog';
import {Dialog} from '../../../../../components/common/dialogs/dialog';
import {DialogButtonGroup} from '../../../../../components/common/dialogs/dialogButtonGroup';
import {DialogContent} from '../../../../../components/common/dialogs/dialogContent';
import {DialogHeader} from '../../../../../components/common/dialogs/dialogHeader';
import {computeIsGlobalNamespace} from '../../../../../helpers/models/namespaceHelpers';
import {useAppDispatch, useAppSelector} from '../../../../../helpers/reduxHelpers';
import {getUser} from '../../../../../store/data/teammates/teammatesSelectors';
import {getUnsavedViewNavigationBlockerId} from '../../../../../store/panels/analytics/analyticsPanelSelectors';
import {getAnalyticsFilters} from '../../../../../store/panels/analytics/filters/analyticsPanelFiltersSelectors';
import {
  getAnalyticsParameters,
  getAnalyticsSelectedNamespace,
  getAnalyticsSelectedNamespaceTeam,
  getAnalyticsSelectedViewId,
} from '../../../../../store/panels/analytics/parameters/analyticsPanelParametersSelectors';
import {
  createSavedViewAsyncAction,
  updateSavedViewAsyncAction,
} from '../../../../../store/panels/analytics/views/analyticsPanelViewsActions';
import {getAnalyticsSavedViews} from '../../../../../store/panels/analytics/views/analyticsPanelViewsSelectors';
import {
  AnalyticsSavedView,
  AnalyticsSelectedViewId,
} from '../../../../../store/panels/analytics/views/analyticsPanelViewsState';
import {navigateToLinkAction} from '../../../../../store/stateless/navigationActions';
import {mapFiltersToTerms} from '../../../../helpers/analyticsSavedViewsHelpers';
import {makeAnalyticsSelectedViewUrl} from '../../../../helpers/analyticsUrlHelpers';
import {AnalyticsNamespaceDropdownBox} from '../../../reportNavigation/analyticsNamespaceDropdownBox';

/*
 * Props.
 */

interface ReportHeaderSaveViewDialogProps {
  onOkClick: Handler;
  onRequestClose: Handler;
  selectedView?: AnalyticsSavedView;
}

/*
 * Style.
 */

const StyledInputLabel = styled.div`
  color: ${(p) => p.theme.greys.shade70};
  margin-top: 5px;
  font-weight: ${fontWeights.semibold};
  font-size: ${fontSizes.small};
  margin-bottom: 4px;

  span {
    color: ${(p) => p.theme.palette.red.shade40};
  }
`;

const StyledCheckBoxWrapper = styled(ChromelessButton)`
  margin: 10px 0;
  display: flex;
`;

const StyledCheckBoxLabel = styled.div`
  margin-left: 8px;
`;

const StyledErrorMessage = styled.div`
  color: ${(p) => p.theme.palette.red.shade40};
  font-size: ${fontSizes.verySmall};
  font-weight: ${fontWeights.semibold};
`;

/*
 * Component.
 */

export const ReportHeaderSaveViewDialog: FC<ReportHeaderSaveViewDialogProps> = (props) => {
  const {onOkClick, onRequestClose, selectedView} = props;

  const dispatch = useAppDispatch();

  const parameters = useAppSelector(getAnalyticsParameters);
  const filters = useAppSelector(getAnalyticsFilters);
  const selectedNamespace = useAppSelector(getAnalyticsSelectedNamespace);
  const isGlobalNamespace = computeIsGlobalNamespace(selectedNamespace);
  const selectedViewId = useAppSelector(getAnalyticsSelectedViewId);
  const user = useAppSelector(getUser);
  const savedViews = useAppSelector(getAnalyticsSavedViews); // We will check the new name against existing views to prevent conflicts.
  const unsavedViewNavigationBlockerId = useAppSelector(getUnsavedViewNavigationBlockerId);

  // selectedNamespace can be the teammate's namespace, in that case currentTeam will be undefined.
  const currentTeam = useAppSelector(getAnalyticsSelectedNamespaceTeam);

  // In the company level view we only display "company" or "individual", and in a team
  // view we only display that team or "individual"
  const currentTeams = currentTeam ? [currentTeam] : [];
  const selectableTeams = isGlobalNamespace ? [] : currentTeams;

  const [isErrorState, setIsErrorState] = useState(false);
  const [viewName, setViewName] = useState(selectedView?.name || '');
  // In edition mode we init the namespace to the view namespace, in creation mode it's undefined, unless we are on our own namespace.
  const [viewNamespace, setViewNamespace] = useState(
    selectedView?.namespace || (!currentTeam && !isGlobalNamespace ? user.namespace : undefined),
  );
  const [hasConflictingName, setHasConflictingName] = useState(false);
  const [isLoading, toggleIsLoading] = useToggle();
  const [isPinned, toggleIsPinned] = useToggle(selectedView?.isPinned || false);
  const [isDefault, toggleIsDefault] = useToggle(selectedView?.isDefault ?? false);

  const onErrorDialogClose = () => {
    setIsErrorState(false);
  };

  const onViewNameChange = (newVal: string) => {
    setHasConflictingName(isNameConflicting(newVal, savedViews, selectedView?.id)); // If name is already taken we prevent saving.
    setViewName(newVal);
  };

  const onSelectedNamespaceChange = (newNamespace: string) => {
    setViewNamespace(newNamespace);
  };

  const saveView = async () => {
    toggleIsLoading();
    let savedViewId: unknown;

    // This should theoretically never happen, because the 'create/save' button should be greyed out if we don't have a viewNamespace.
    if (!viewNamespace) {
      setIsErrorState(true);
      return undefined;
    }

    // If selected view is not defined, we must create a new saved view.
    if (!selectedView) {
      savedViewId = await dispatch(
        createSavedViewAsyncAction(
          {
            name: viewName,
            filters: {terms: mapFiltersToTerms(filters)},
            isPinned,
            isDefault,
            namespace: viewNamespace,
            visibilityNamespace: selectedNamespace,
            schedulingConfig: null,
          },
          viewNamespace,
          selectedViewId,
        ),
      );
    } else {
      savedViewId = await dispatch(
        updateSavedViewAsyncAction(
          {
            ...selectedView,
            name: viewName,
            isPinned,
            isDefault,
          },
          viewNamespace,
        ),
      );
    }

    // TODO navigation should be triggered in createSavedViewAsyncAction and updateSavedViewAsyncAction
    // after cleaning circular dependencies in views action.
    if (typeof savedViewId === 'number') {
      const path = makeAnalyticsSelectedViewUrl(savedViewId, parameters);
      dispatch(navigateToLinkAction(path, undefined, unsavedViewNavigationBlockerId));
    }

    return onOkClick();
  };

  const NamespaceDropdownPlaceholder = isGlobalNamespace ? (
    <Trans id="dialog-save-view-user-namespace-placeholder-company">Select company or make it private</Trans>
  ) : (
    <Trans id="dialog-save-view-user-namespace-placeholder team">Select a team or make it private</Trans>
  );

  return (
    // Width is 305 here to avoid clipping in the namespace dropdown placeholder
    <Dialog {...props} width={305}>
      {isErrorState && (
        <AlertDialog
          title={<Trans id="analytics-save-report-view-error-dialog-title">Failed to save the view</Trans>}
          onRequestClose={onErrorDialogClose}
        >
          <Trans id="analytics-save-report-view-error-dialog-message">Access was not selected</Trans>
        </AlertDialog>
      )}
      <DialogHeader title={selectedView ? 'Edit view' : 'Create view'} />
      <DialogContent>
        <BoxedInput
          shouldAutoFocus
          hasError={hasConflictingName}
          errorMessage={
            <StyledErrorMessage>
              <Trans id="dialog-save-new-view-conflict-error">A view with this name already exists.</Trans>
            </StyledErrorMessage>
          }
          label={<Trans id="dialog-save-view-name-label">Name</Trans>}
          isRequired
        >
          {(childProps) => (
            <BoxedInputElement
              {...childProps}
              value={viewName}
              onChange={onViewNameChange}
              type={InputTypesEnum.TEXT}
              size={VisualSizesEnum.SMALL}
            />
          )}
        </BoxedInput>

        <StyledInputLabel>
          <Trans id="dialog-save-view-access-label">Access</Trans>&nbsp;<span>*</span>
        </StyledInputLabel>
        <AnalyticsNamespaceDropdownBox
          teams={selectableTeams}
          userNamespace={user.namespace}
          selectedNamespace={viewNamespace}
          onNamespaceSelect={onSelectedNamespaceChange}
          userNamespaceLabel={<Trans id="dialog-save-view-user-namespace">Individual view</Trans>}
          placeholder={NamespaceDropdownPlaceholder}
          shouldIncludeCompanyDropdownItem={isGlobalNamespace}
          // We don't allow editing the namespace for a view that has already been created.
          isDisabled={isDefined(selectedView)}
        />

        <StyledCheckBoxWrapper onClick={toggleIsPinned}>
          <CheckBox isChecked={isPinned} onChange={noop} />
          <StyledCheckBoxLabel>
            <Trans id="dialog-save-view-pin">Pin to my favorites</Trans>
          </StyledCheckBoxLabel>
        </StyledCheckBoxWrapper>

        <StyledCheckBoxWrapper onClick={toggleIsDefault}>
          <CheckBox isChecked={isDefault} onChange={noop} />
          <StyledCheckBoxLabel>
            <Trans id="dialog-save-view-default">Make default view</Trans>
          </StyledCheckBoxLabel>
        </StyledCheckBoxWrapper>
      </DialogContent>
      <DialogButtonGroup {...props}>
        <ButtonGroupSpacer />
        <SameWidthButton onClick={onRequestClose}>
          <Trans id="dialog-save-view-cancel">Cancel</Trans>
        </SameWidthButton>
        <SameWidthButton
          variant={ButtonVariantsEnum.PRIMARY}
          onClick={saveView}
          isDisabled={!viewName.trim() || !viewNamespace || isLoading || hasConflictingName}
        >
          {selectedView ? (
            <Trans id="dialog-save-view-ok-edit">Edit</Trans>
          ) : (
            <Trans id="dialog-save-view-ok-creat">Create</Trans>
          )}
        </SameWidthButton>
      </DialogButtonGroup>
    </Dialog>
  );
};

/*
 * Helpers.
 */

function isNameConflicting(
  viewName: string,
  existingViews: ReadonlyArray<AnalyticsSavedView>,
  selectedViewId?: AnalyticsSelectedViewId,
) {
  return existingViews.some(
    ({name, id}) =>
      name.trim().toLocaleLowerCase() === viewName.trim().toLocaleLowerCase() && id !== selectedViewId,
  );
}
